// src/api/ismApi.js — Backend API client (aligned with new backend)
import { apiFetch, apiFetchWithMeta, API_BASE } from '../shared/api/httpClient';

// ── Auth ────────────────────────────────────────

export async function loginApi(email, password) {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Login failed (${res.status})`);
  }
  return res.json();
}

export async function ssoLoginApi(microsoftAccessToken) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  try {
    const res = await fetch(`${API_BASE}/api/auth/sso-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: microsoftAccessToken }),
      signal: controller.signal,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `SSO login failed (${res.status})`);
    }
    const data = await res.json();
    return data;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function changePasswordApi({ old_password, new_password, confirm_new_password }) {
  return apiFetch(`${API_BASE}/api/auth/change-password`, {
    method: 'POST',
    body: JSON.stringify({ old_password, new_password, confirm_new_password }),
  });
}

// ── Tickets ────────────────────────────────────

const MAX_TICKET_PAGE_SIZE = 200;

function buildTicketQuery({
  limit = 20,
  skip = 0,
  startDate,
  endDate,
  categoryRefId,
  picId,
  statuses,
  priorities,
  flag,
  search,
  sortBy,
  sortOrder,
} = {}) {
  const safeLimit = Math.min(Math.max(Number(limit) || 50, 1), MAX_TICKET_PAGE_SIZE);
  const safeSkip = Math.max(Number(skip) || 0, 0);
  const params = new URLSearchParams({
    limit: String(safeLimit),
    skip: String(safeSkip),
  });

  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (categoryRefId && categoryRefId !== 'all') params.set('category_ref_id', categoryRefId);
  if (picId && picId !== 'all' && picId !== 'unassigned') params.set('pic_id', picId);
  (statuses || []).filter(status => status && status !== 'all')
    .forEach(status => params.append('status', status));
  (priorities || []).filter(priority => priority && priority !== 'all')
    .forEach(priority => params.append('priority', priority));
  if (sortBy) params.set('sort_by', sortBy);
  if (sortOrder) params.set('sort_order', sortOrder);
  if (search?.trim()) params.set('search', search.trim());
  if (flag) params.set('flag', flag);

  return params;
}

export async function fetchTickets(options = {}) {
  const params = buildTicketQuery(options);
  return apiFetch(`${API_BASE}/api/tickets?${params.toString()}`, {
    signal: options.signal,
    timeout: options.timeout,
  });
}

export async function fetchTicketsByUserId({ userId, ...options } = {}) {
  const params = buildTicketQuery(options);
  return apiFetch(`${API_BASE}/api/tickets/by-user/${encodeURIComponent(userId)}?${params.toString()}`, {
    signal: options.signal,
    timeout: options.timeout,
  });
}

export async function fetchTicket(ticketId, options = {}) {
  return apiFetch(`${API_BASE}/api/tickets/${encodeURIComponent(ticketId)}`, options);
}

// Returns { status, headers, data } so callers can read the `X-Next-Cursor`
// header on a bodyless 204 as well as the normal 200 JSON body.
export async function fetchTicketsSince({ cursor, afterId, includeState = false, signal } = {}) {
  const params = new URLSearchParams({ cursor });
  if (afterId) params.set('after_id', afterId);
  if (includeState) params.set('include_state', '1');
  return apiFetchWithMeta(`${API_BASE}/api/tickets/since?${params.toString()}`, {
    signal,
    timeout: 15000,
  });
}

export async function updateTicketStatus(ticketId, data) {
  return apiFetch(`${API_BASE}/api/tickets/${ticketId}/status`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateTicketAssignee(ticketId, data) {
  return apiFetch(`${API_BASE}/api/tickets/${ticketId}/assignee`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateTicketCategory(ticketId, data) {
  return apiFetch(`${API_BASE}/api/tickets/${ticketId}/category`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function fetchTicketMessages(ticketId) {
  return apiFetch(`${API_BASE}/api/tickets/${ticketId}/messages`);
}

export async function fetchTicketActivities(ticketId) {
  return apiFetch(`${API_BASE}/api/tickets/${ticketId}/activities`);
}

export async function fetchNotRelatedTickets({ limit = 20, skip = 0, signal, timeout } = {}) {
  const params = buildTicketQuery({ limit, skip });
  return apiFetch(`${API_BASE}/api/tickets/not-related?${params.toString()}`, {
    signal,
    timeout,
  });
}

// ── Dashboard analytics ─────────────────────────

export async function fetchDashboardSummary(options = {}) {
  const params = new URLSearchParams();
  if (options.startDate) params.set('start_date', options.startDate);
  if (options.endDate) params.set('end_date', options.endDate);
  if (options.categoryRefId && options.categoryRefId !== 'all') {
    params.set('category_ref_id', options.categoryRefId);
  }
  if (options.picId && options.picId !== 'all' && options.picId !== 'unassigned') {
    params.set('pic_id', options.picId);
  }
  (options.statuses || []).filter(status => status && status !== 'all')
    .forEach(status => params.append('status', status));
  (options.priorities || []).filter(priority => priority && priority !== 'all')
    .forEach(priority => params.append('priority', priority));
  if (options.search?.trim()) params.set('search', options.search.trim());
  if (options.trendDays) params.set('trend_days', String(options.trendDays));
  if (options.heatmapDays) params.set('heatmap_days', String(options.heatmapDays));

  const query = params.toString();
  return apiFetch(`${API_BASE}/api/dashboard/summary${query ? `?${query}` : ''}`, {
    signal: options.signal,
    timeout: options.timeout,
  });
}

export async function fetchAgingAnalysis({
  startDate,
  endDate,
  categoryRefId,
  picId,
  statuses,
  signal,
  timeout,
} = {}) {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (categoryRefId) params.set('category_ref_id', categoryRefId);
  if (picId && picId !== 'all' && picId !== 'unassigned') {
    params.set('pic_id', picId);
  }
  (statuses || []).forEach(status => params.append('status', status));

  const qs = params.toString();
  return apiFetch(
    `${API_BASE}/api/tickets/aging-analysis${qs ? `?${qs}` : ''}`,
    { signal, timeout },
  );
}

// ── Categories ─────────────────────────────────

export async function fetchCategories({ activeOnly = false, signal } = {}) {
  const qs = activeOnly ? '?active_only=true' : '';
  return apiFetch(`${API_BASE}/api/categories${qs}`, { signal });
}

export async function fetchCategory(categoryId) {
  return apiFetch(`${API_BASE}/api/categories/${categoryId}`);
}

export async function fetchCategoryByUserId(userId) {
  return apiFetch(`${API_BASE}/api/categories/by-user/${userId}`);
}

export async function createCategory(data) {
  return apiFetch(`${API_BASE}/api/categories`, { method: 'POST', body: JSON.stringify(data) });
}

export async function updateCategory(id, data) {
  return apiFetch(`${API_BASE}/api/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteCategory(id) {
  return apiFetch(`${API_BASE}/api/categories/${id}`, { method: 'DELETE' });
}

export const adminFetchCategories = fetchCategories;
export const adminCreateCategory  = createCategory;
export const adminUpdateCategory  = updateCategory;
export const adminDeleteCategory  = deleteCategory;

/** Normal-user: categories visible to a specific user */
export function getUserCategories(userId) {
  return fetchCategoryByUserId(userId);
}

/** Normal-user: tickets belonging to a specific user */
export function getUserTickets(userId, { limit = 20, skip = 0 } = {}) {
  return fetchTicketsByUserId({ userId, limit, skip });
}

// ── Statuses ───────────────────────────────────

export async function fetchStatuses({ signal } = {}) {
  return apiFetch(`${API_BASE}/api/statuses`, { signal });
}

// ── SLA Policies ───────────────────────────────

export async function fetchSlaPolicies() {
  return apiFetch(`${API_BASE}/api/sla-policies`);
}

export async function createSlaPolicy(data) {
  return apiFetch(`${API_BASE}/api/sla-policies`, { method: 'POST', body: JSON.stringify(data) });
}

export async function updateSlaPolicy(id, data) {
  return apiFetch(`${API_BASE}/api/sla-policies/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteSlaPolicy(id) {
  return apiFetch(`${API_BASE}/api/sla-policies/${id}`, { method: 'DELETE' });
}

// ── Teams ──────────────────────────────────────

export async function fetchTeams() {
  return apiFetch(`${API_BASE}/api/teams`);
}

export async function createTeam(data) {
  return apiFetch(`${API_BASE}/api/teams`, { method: 'POST', body: JSON.stringify(data) });
}

// ── Roles ──────────────────────────────────────

export async function fetchRoles() {
  return apiFetch(`${API_BASE}/api/roles`);
}

export async function createRole(data) {
  return apiFetch(`${API_BASE}/api/roles`, { method: 'POST', body: JSON.stringify(data) });
}

// ── Users ──────────────────────────────────────

export async function fetchUsers({ signal } = {}) {
  return apiFetch(`${API_BASE}/api/users`, { signal });
}

export async function createUser(data) {
  return apiFetch(`${API_BASE}/api/users`, { method: 'POST', body: JSON.stringify(data) });
}

export async function updateUser(id, data) {
  return apiFetch(`${API_BASE}/api/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteUser(id) {
  return apiFetch(`${API_BASE}/api/users/${id}`, { method: 'DELETE' });
}

// ── Category PIC Maps ──────────────────────────

export async function fetchCategoryPicMaps({ signal } = {}) {
  return apiFetch(`${API_BASE}/api/category-pic-maps`, { signal });
}

export async function createCategoryPicMap(data) {
  return apiFetch(`${API_BASE}/api/category-pic-maps`, { method: 'POST', body: JSON.stringify(data) });
}

export async function updateCategoryPicMap(id, data) {
  return apiFetch(`${API_BASE}/api/category-pic-maps/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteCategoryPicMap(id) {
  return apiFetch(`${API_BASE}/api/category-pic-maps/${id}`, { method: 'DELETE' });
}
